import { createSlice } from '@reduxjs/toolkit';
import type { AppThunkDispatch, RootState } from '../..';
import { createReferenceEqualSelector } from '../../selectors/utils';
import type { CatalogCategories } from '../../../schemas/catalogs';
import {
  CATEGORY_TYPE_ACCESSORIES,
  CATEGORY_TYPE_FURNITURE,
  CATEGORY_TYPE_SYSTEM,
} from '../../../utils/category/categoryTypes';

type statsState = {
  [key: string]: {
    autoCategorisable: number | null;
    categoryTypes: {
      system: number;
      furniture: number;
      accessories: number;
    } | null;
  };
};

const initialState: statsState = {};

const statisticsSlice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {
    setAutoCategorisable(
      state,
      action: { payload: { autoCategorisable: number; catalogId: string } },
    ) {
      return {
        ...state,
        [action.payload.catalogId]: {
          ...state[action.payload.catalogId],
          autoCategorisable: action.payload.autoCategorisable,
        },
      };
    },
    setCategoryTypes(
      state,
      action: {
        payload: {
          categoryTypes: { system: number; furniture: number; accessories: number };
          catalogId: string;
        };
      },
    ) {
      return {
        ...state,
        [action.payload.catalogId]: {
          ...state[action.payload.catalogId],
          categoryTypes: action.payload.categoryTypes,
        },
      };
    },
  },
});

export const { setAutoCategorisable, setCategoryTypes } = statisticsSlice.actions;
export default statisticsSlice.reducer;

export const selectStatistics = (state: RootState, catalogId: string) =>
  state.statistics[catalogId];

export const selectAutoCategorisable = createReferenceEqualSelector(
  selectStatistics,
  (state) => state.autoCategorisable,
);

export const selectCategoryTypes = createReferenceEqualSelector(
  selectStatistics,
  (state) => state.categoryTypes,
);

export const setCategoryStatistics =
  (catalogId: string, categories: CatalogCategories['categories']) =>
  (dispatch: AppThunkDispatch) => {
    const categoryCounters = {
      autoCategorisable: 0,
      categoryTypes: {
        system: 0,
        furniture: 0,
        accessories: 0,
      },
    };

    for (let i = 0; i < categories.length; i++) {
      const stats = categories[i];

      if (stats.autoCategorisable) categoryCounters.autoCategorisable++;

      switch (stats.categoryType) {
        case CATEGORY_TYPE_SYSTEM:
          categoryCounters.categoryTypes.system++;
          break;
        case CATEGORY_TYPE_FURNITURE:
          categoryCounters.categoryTypes.furniture++;
          break;
        case CATEGORY_TYPE_ACCESSORIES:
          categoryCounters.categoryTypes.accessories++;
          break;
      }
    }

    const { autoCategorisable, categoryTypes } = categoryCounters;
    dispatch(setCategoryTypes({ catalogId, categoryTypes }));

    dispatch(
      setAutoCategorisable({
        catalogId,
        autoCategorisable,
      }),
    );
  };
